import axios from 'axios'
import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

interface User {
    id: string
    name: string
    cpf: string
    position: string
    branch?: string
    branchCode?: string
    initials?: string
    email?: string
}

interface AuthContextData {
    user: User | null
    loading: boolean
    login: (cpf: string, password: string, twoFactorCode?:string) => Promise<LoginResult>
    logout: () => void
    isAdmin: boolean
}

interface LoginResult {
    requiresTwoFactor?: boolean
    codeSent?: boolean
}

const BASE_URL = axios.create({
    baseURL: 'https://academia.gazin.ai/api/',
    // baseURL: 'http://localhost:3000/'
})
// Injeta token em todas as requisições
BASE_URL.interceptors.request.use((config) => {
    const token = localStorage.getItem('token')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
})

// Verifica token expirado
BASE_URL.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response?.status === 401 || err.response?.status === 403) {
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            window.location.href = '/login'
        }
        return Promise.reject(err)
    }
)
const AuthContext = createContext<AuthContextData>({} as AuthContextData)

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    useEffect(() => {
        const stored = localStorage.getItem('user')
        const token = localStorage.getItem('token')
        if (stored && token) {
            setUser(JSON.parse(stored))
        }
        setLoading(false)
    }, [])

    async function login(cpf: string, password: string, twoFactorCode?: string): Promise<LoginResult> {
        const { data } = await BASE_URL.post('/login', { cpf, password, twoFactorCode })

        // ainda falta o código -> não salva nada, só devolve o sinal pro componente
        if (data.requiresTwoFactor) {
            return { requiresTwoFactor: true, codeSent: data.codeSent }
        }

        localStorage.setItem('token', data.token)

        const userData: User = {
            id: data.user.id,
            name: data.user.name,
            cpf: data.user.cpf,
            position: data.user.position,
            branch: data.user.branch?.name,
            branchCode: data.user.branch?.code,
            email: data.user.email,
            initials: data.user.name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase(),
        }

        localStorage.setItem('user', JSON.stringify(userData))
        setUser(userData)

        return {}
    }

    function logout() {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        window.location.href = '/login'

        setUser(null)
    }

    const isAdmin = ['admin', 'gerente_nacional', 'gerente_regional', 'gerente_loja'].includes(user?.position || '')

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, isAdmin }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)